import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Crown, Award, Star, TrendingUp, Sparkles,
  CheckCircle2, Loader2
} from 'lucide-react';
import api from '@/utils/api';

const tierConfig = {
  new_seller: {
    icon: Sparkles,
    color: 'text-slate-500',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-200',
    gradient: 'from-slate-50 to-slate-100'
  },
  rising_seller: {
    icon: TrendingUp,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
    gradient: 'from-blue-50 to-indigo-100'
  },
  trusted_seller: {
    icon: Star,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-100',
    borderColor: 'border-emerald-200',
    gradient: 'from-emerald-50 to-teal-100'
  },
  power_seller: {
    icon: Award,
    color: 'text-purple-500',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-200',
    gradient: 'from-purple-50 to-pink-100'
  },
  top_seller: {
    icon: Crown,
    color: 'text-amber-500',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-200',
    gradient: 'from-amber-50 to-orange-100'
  }
};

/**
 * Seller Tier Badge - Small badge for listings and profiles
 */
export const SellerTierBadge = ({ tier, size = 'sm' }) => {
  const config = tierConfig[tier] || tierConfig.new_seller;
  const Icon = config.icon;
  
  const tierDisplay = {
    new_seller: 'New Seller',
    rising_seller: 'Rising',
    trusted_seller: 'Trusted',
    power_seller: 'Power Seller',
    top_seller: 'Top Seller'
  };
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5'
  };
  
  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };
  
  return (
    <Badge variant="outline" className={`${config.bgColor} ${config.borderColor} ${config.color} ${sizeClasses[size]}`}>
      <Icon className={`${iconSizes[size]} mr-1`} />
      {tierDisplay[tier]}
    </Badge>
  );
};

/**
 * Seller Tier Card - Full card showing tier progress and perks
 */
export const SellerTierCard = ({ className = '' }) => {
  const [tierData, setTierData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchTierData = async () => {
      try {
        const res = await api.get('/seller/tier');
        setTierData(res.data);
      } catch (error) {
        console.error('Failed to fetch seller tier:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTierData();
  }, []);
  
  if (loading) {
    return (
      <Card className={`p-6 ${className}`}>
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </Card>
    );
  }
  
  if (!tierData) return null;
  
  const config = tierConfig[tierData.tier] || tierConfig.new_seller;
  const Icon = config.icon;
  
  const nextTierDisplay = {
    rising_seller: 'Rising Seller',
    trusted_seller: 'Trusted Seller',
    power_seller: 'Power Seller',
    top_seller: 'Top Seller'
  };
  
  return (
    <Card className={`p-6 bg-gradient-to-r ${config.gradient} border ${config.borderColor} ${className}`}>
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-full ${config.bgColor}`}>
          <Icon className={`h-8 w-8 ${config.color}`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-lg">{tierData.tier_display}</h3>
            <SellerTierBadge tier={tierData.tier} size="sm" />
          </div>
          
          {/* Progress to next tier */}
          {tierData.next_tier && (
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">Progress to {nextTierDisplay[tierData.next_tier]}</span>
                <span className="font-medium">{tierData.progress_to_next}%</span>
              </div>
              <Progress value={tierData.progress_to_next} className="h-2" />
            </div>
          )}
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-4 text-center">
            <div className="p-2 bg-white/50 rounded-lg">
              <p className="text-xl font-bold">{tierData.stats?.sales_count || 0}</p>
              <p className="text-xs text-slate-500">Sales</p>
            </div>
            <div className="p-2 bg-white/50 rounded-lg">
              <p className="text-xl font-bold">{tierData.stats?.avg_rating || '-'}</p>
              <p className="text-xs text-slate-500">Rating</p>
            </div>
            <div className="p-2 bg-white/50 rounded-lg">
              <p className="text-xl font-bold">{tierData.stats?.trust_score || 0}</p>
              <p className="text-xs text-slate-500">Trust Score</p>
            </div>
          </div>
          
          {/* Perks */}
          <div>
            <h4 className="font-semibold text-sm mb-2">Your Perks:</h4>
            <ul className="space-y-1">
              {tierData.perks?.slice(0, 3).map((perk, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 className={`h-4 w-4 ${config.color}`} />
                  {perk}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SellerTierCard;
